import React, { Fragment, PropsWithChildren, ReactNode, useMemo } from 'react';
import {
  ColumnDef,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  OnChangeFn,
  PaginationState,
  Row,
  SortingState,
  TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import {
  DefaultTableContainer,
  TableCell,
  TableHeaderCell,
  TableRow,
} from './Table.styles';
import Pagination, { PaginationProps } from './Pagination';
import { ChevronDown, Download } from 'react-ionicons';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import Loader from '../Loader';
import { clsx } from 'clsx';
import { getAlignmentClass } from './utils';

enum TableVariant {
  default = '56px',
  small = '40px',
}

const TableMessage = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => (
  <div
    className={clsx(
      'col-span-full flex min-h-[570px] w-full items-center justify-center',
      className,
    )}
  >
    {children}
  </div>
);
export const DataLoading = ({ className }: { className?: string }) => (
  <TableMessage className={className}>
    <Loader />
  </TableMessage>
);

const FetchingError = () => (
  <TableMessage>There was an error loading the results.</TableMessage>
);

const EmptyData = () => <TableMessage>No results found.</TableMessage>;

export interface ServerPaginatedTableProps<T extends object> {
  data: T[];
  columns: ColumnDef<T>[];
  title: string;
  handleRowClick?: (row: Row<T>) => void;
  paginationState: PaginationState;
  onPaginationChange: OnChangeFn<PaginationState>;
  reactTableOptions?: TableOptions<T>;
  itemCount: number;
  pageCount: number;
  sortingState?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  isFetchingData?: boolean;
  isFetchingError?: boolean;
  downloadTableFn?: () => Promise<void>;
  showHeadersWhenEmpty?: boolean;
  showPagination?: boolean;
  emptyDataElement?: ReactNode;
  tourClassName?: string;
  paginationChanged?: (pageSize: number) => void;
  paginationSizeStored?: number;
  elementBeforePagination?: ReactNode;
  paginationVariant?: PaginationProps['variant'];
  variant?: 'small' | 'default';
  pageSizeMenuPlacement?: PaginationProps['pageSizeMenuPlacement'];
  tableContainerClassName?: string;
  columnGap?: string;
  loaderContainerClassName?: string;
  styleVariant?: 'default' | 'grid';
  subRows?: {
    name: string;
    renderSubComponent: (props: {
      row: Row<T>;
      expanded?: ExpandedState;
      isLastRow: boolean;
    }) => React.ReactElement;
  };
  getRowClassName?: (row: Row<T>) => string;
  hidePageSizeSelection?: boolean;
  showRowCount?: boolean;
}

export const ServerPaginatedTable = <T extends object>({
  data,
  columns,
  paginationState,
  onPaginationChange,
  itemCount,
  pageCount,
  title,
  sortingState,
  onSortingChange,
  isFetchingData,
  isFetchingError,
  handleRowClick,
  downloadTableFn,
  showHeadersWhenEmpty = true,
  showPagination = true,
  emptyDataElement = <EmptyData />,
  tourClassName,
  paginationSizeStored,
  paginationChanged,
  elementBeforePagination,
  paginationVariant,
  variant = 'default',
  pageSizeMenuPlacement,
  tableContainerClassName,
  columnGap,
  loaderContainerClassName,
  styleVariant = 'default',
  subRows,
  getRowClassName,
  hidePageSizeSelection,
  showRowCount = true,
}: ServerPaginatedTableProps<T>) => {
  const [expanded, setExpanded] = React.useState<ExpandedState>({});

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    enableSortingRemoval: false,
    sortDescFirst: true,
    onPaginationChange,
    onSortingChange,
    pageCount,
    state: {
      pagination: paginationState,
      sorting: sortingState,
      expanded,
    },
    ...(subRows
      ? {
          getExpandedRowModel: getExpandedRowModel(),
          getRowCanExpand: () => true,
          onExpandedChange: setExpanded,
        }
      : {}),
  });
  const displayHeaders = showHeadersWhenEmpty ? true : data.length > 0;

  const render = useMemo(() => {
    if (isFetchingData)
      return <DataLoading className={loaderContainerClassName} />;
    if (isFetchingError) return <FetchingError />;
    if (data.length === 0) return emptyDataElement;
    else
      return (
        <DefaultTableContainer
          $columnCount={columns.length}
          variant={TableVariant[variant]}
          className={clsx(
            'overflow-x-auto scrollbar-thin scrollbar-track-background-base-surface-1 scrollbar-thumb-stroke-base-subdued',
            tableContainerClassName,
            {
              '!gap-0': styleVariant === 'grid',
            },
          )}
          $columnGap={columnGap}
        >
          {displayHeaders &&
            table.getHeaderGroups().map((headerGroup) => (
              <Fragment key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <TableHeaderCell
                    key={header.id}
                    className={clsx(
                      header.column.getCanSort() && 'cursor-pointer gap-2',
                      getAlignmentClass(header.column.columnDef.meta?.align),
                      {
                        '!mb-0 justify-between border-r border-t border-stroke-base-default px-4':
                          styleVariant === 'grid',
                        'rounded-tr':
                          index + 1 === headerGroup.headers.length &&
                          styleVariant === 'grid',
                        'rounded-tl border-l':
                          index === 0 && styleVariant === 'grid',
                      },
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                    role="columnheader"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                    {header.column.getCanSort() ? (
                      <ChevronDown
                        cssClasses={
                          header.column.getIsSorted() === 'asc'
                            ? 'rotate-180'
                            : ''
                        }
                        width="16px"
                        height="16px"
                        color={`rgb(var(--${
                          header.column.getIsSorted()
                            ? 'content-accent-default'
                            : 'content-base-subdued'
                        }))`}
                      />
                    ) : null}
                  </TableHeaderCell>
                ))}
              </Fragment>
            ))}
          {table.getRowModel().rows.map((row, index) => (
            <>
              <TableRow
                key={row.id}
                onClick={() => handleRowClick && handleRowClick(row)}
                role="row"
                variant={TableVariant[variant]}
                className={clsx(
                  'group/row odd:py-2',
                  index === 0 && tourClassName,
                  {
                    '!gap-0 border-x border-t border-stroke-base-default !p-0 last:rounded-b last:!border-b':
                      styleVariant === 'grid',
                  },
                  getRowClassName?.(row),
                )}
                style={
                  styleVariant === 'grid' &&
                  table.getRowModel().rows.length !== index + 1
                    ? { borderBottom: 'none' }
                    : {}
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    role="cell"
                    aria-rowindex={index}
                    className={clsx(
                      getAlignmentClass(cell.column.columnDef.meta?.align),
                      {
                        'border-r border-stroke-base-default px-4  last:border-none':
                          styleVariant === 'grid',
                      },
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
              {subRows?.renderSubComponent &&
                subRows?.renderSubComponent({
                  row,
                  expanded,
                  isLastRow: table.getRowModel().rows.length === index + 1,
                })}
            </>
          ))}
        </DefaultTableContainer>
      );
  }, [
    data.length,
    emptyDataElement,
    isFetchingData,
    isFetchingError,
    columns.length,
    displayHeaders,
    tableContainerClassName,
    variant,
    table,
    handleRowClick,
    tourClassName,
    columnGap,
    loaderContainerClassName,
    subRows,
    getRowClassName,
    styleVariant,
    expanded,
  ]);

  return (
    <div
      className="flex h-full w-full flex-col items-start justify-start"
      role="table"
      aria-label={title}
    >
      <div className="w-full flex-1">{render}</div>
      {elementBeforePagination ? elementBeforePagination : null}
      <div
        className={clsx(
          'mt-6 flex w-full flex-col items-start justify-start',
          !showPagination && 'hidden',
        )}
      >
        <Pagination
          pageCount={table.getPageCount()}
          totalRows={itemCount}
          currentPage={table.getState().pagination.pageIndex + 1 || 1}
          onPageChange={(page: number) => table.setPageIndex(page - 1)}
          currentRowsShown={data.length}
          paginationSizeStored={paginationSizeStored}
          pageSizeChanged={(page: number) => {
            table.setPageSize(page);
            paginationChanged?.(page);
          }}
          pageSizeMenuPlacement={pageSizeMenuPlacement}
          variant={paginationVariant}
          hidePageSizeSelection={hidePageSizeSelection}
          showRowCount={showRowCount}
        />
      </div>
      {downloadTableFn && (
        <div className="mt-6 flex w-full items-center justify-end">
          <GraniteButton variant="secondary" onClick={downloadTableFn}>
            Export table
            <span className="mb-0.5">
              <Download width="20px" height="20px" />
            </span>
          </GraniteButton>
        </div>
      )}
    </div>
  );
};
